/**
 * Shared report calculations for Trial Balance, Income Statement, Financial Position.
 *
 * Accounting model (reconstructed double-entry):
 *   Bill          DR Debtors / CR Revenue
 *   Receipt       DR Bank    / CR Debtors
 *   Supplier inv  DR Exp/Asset / CR Creditors
 *   Supplier pay  DR Creditors / CR Bank
 *   Opening cash  DR Bank    / CR Accumulated Fund
 *   Journal       DR to_coa  / CR from_coa  (bank side adjusts Cash at Bank)
 *
 * TB balances when cashbook income/expense align with receipts / supplier payments
 * (excluding transfers and opening). Orphans from incomplete deletes break the TB.
 */

export const CONTROL_ACCOUNTS = {
  DEBTORS: '321030',
  BANK: '326040',
  CREDITORS: '411110',
  ACCUMULATED_FUND: '510010',
};

export const CONTROL_ACCOUNT_LABELS = {
  [CONTROL_ACCOUNTS.DEBTORS]: 'Debtors Control Account',
  [CONTROL_ACCOUNTS.BANK]: 'Cash at Bank',
  [CONTROL_ACCOUNTS.CREDITORS]: 'Creditors Control Account',
  [CONTROL_ACCOUNTS.ACCUMULATED_FUND]: 'Accumulated Fund',
};

const CONTROL_CODES = new Set(Object.values(CONTROL_ACCOUNTS));

export const sumArray = (arr) => arr.reduce((sum, num) => sum + (parseFloat(num) || 0), 0);

export const getBudgetCoa = (budgetId, allData) =>
  allData.budget?.find((b) => String(b.id) === String(budgetId))?.coa;

export const getCoaRecord = (code, allData) =>
  allData.coa?.find((c) => String(c.account) === String(code));

export const getCoaName = (code, allData) =>
  getCoaRecord(code, allData)?.name ||
  CONTROL_ACCOUNT_LABELS[code] ||
  'Unknown';

export const isControlAccount = (code) => CONTROL_CODES.has(String(code));

export const classifyAccount = (code, allData) => {
  if (String(code) === CONTROL_ACCOUNTS.ACCUMULATED_FUND) return 'equity';
  if (String(code) === CONTROL_ACCOUNTS.BANK) return 'asset';
  if (String(code) === CONTROL_ACCOUNTS.DEBTORS) return 'asset';
  if (String(code) === CONTROL_ACCOUNTS.CREDITORS) return 'liability';

  const record = getCoaRecord(code, allData);
  const type = record?.type?.toLowerCase() || '';
  if (type.includes('rev')) return 'revenue';
  if (type.includes('lia')) return 'liability';
  if (type.includes('ass')) return 'asset';
  if (type.includes('exp')) return 'expense';
  const digit = String(code)?.[0];
  if (digit === '1') return 'revenue';
  if (digit === '2') return 'expense';
  if (digit === '3') return 'asset';
  if (digit === '4') return 'liability';
  if (digit === '5') return 'equity';
  return 'expense';
};

export const isPlAccount = (code, allData) => {
  const cls = classifyAccount(code, allData);
  return cls === 'revenue' || cls === 'expense';
};

const mergeByKey = (openingArr = [], periodArr = [], keyFn) => {
  const map = new Map();
  [...openingArr, ...periodArr].forEach((row) => {
    const key = keyFn(row);
    const amount = parseFloat(row.amount) || 0;
    if (map.has(key)) {
      map.get(key).amount += amount;
    } else {
      map.set(key, { ...row, amount });
    }
  });
  return Array.from(map.values());
};

export const mergeBills = (openingBills = [], bills = []) =>
  mergeByKey(openingBills, bills, (r) => r.coa_id);

export const mergeSupplier = (openingSupplier = [], supplier = []) =>
  mergeByKey(openingSupplier, supplier, (r) => `${r.coa}_${r.type}_${r.expense}`);

export const mergeScalarAmount = (openingArr = [], periodArr = []) =>
  (parseFloat(openingArr?.[0]?.amount) || 0) + (parseFloat(periodArr?.[0]?.amount) || 0);

export const mergeJournals = (openingJournal = [], journal = []) =>
  [...(openingJournal || []), ...(journal || [])];

const ensureBalance = (balances, code, allData) => {
  if (!balances.has(code)) {
    balances.set(code, {
      code,
      desc: getCoaName(code, allData),
      debit: 0,
      credit: 0,
    });
  }
  return balances.get(code);
};

const signedToDebitCredit = (code, signedAmount, allData, forceSide = null) => {
  const cls = forceSide || classifyAccount(code, allData);
  if (signedAmount === 0) return { debit: 0, credit: 0 };

  // Credit-normal: revenue, liability, equity
  if (cls === 'revenue' || cls === 'liability' || cls === 'equity') {
    return signedAmount >= 0
      ? { debit: 0, credit: signedAmount }
      : { debit: -signedAmount, credit: 0 };
  }
  // Debit-normal: asset, expense
  return signedAmount >= 0
    ? { debit: signedAmount, credit: 0 }
    : { debit: 0, credit: -signedAmount };
};

const addSignedAmount = (balances, code, signedAmount, allData, forceSide = null) => {
  if (!code || isControlAccount(code)) return;
  const { debit, credit } = signedToDebitCredit(code, signedAmount, allData, forceSide);
  const entry = ensureBalance(balances, code, allData);
  entry.debit += debit;
  entry.credit += credit;
};

/**
 * Apply journal. Bank (326040) movements are returned as bankAdj
 * (positive = increase bank / debit) so they merge into Cash at Bank
 * instead of creating a separate unbalanced side.
 */
export const applyJournalEntry = (balances, entry, allData) => {
  const amount = parseFloat(entry.amount) || 0;
  const fromCode = getBudgetCoa(entry.from_coa, allData);
  const toCode = getBudgetCoa(entry.to_coa, allData);
  let bankAdj = 0;

  if (fromCode === CONTROL_ACCOUNTS.BANK) {
    bankAdj -= amount;
  } else if (fromCode) {
    addSignedAmount(balances, fromCode, -amount, allData);
  }

  if (toCode === CONTROL_ACCOUNTS.BANK) {
    bankAdj += amount;
  } else if (toCode) {
    addSignedAmount(balances, toCode, amount, allData);
  }

  return bankAdj;
};

export const netTrialBalanceRow = (entry) => {
  const debit = entry.debit || 0;
  const credit = entry.credit || 0;
  if (debit !== 0 && credit !== 0) {
    const difference = debit - credit;
    if (difference > 0) return { ...entry, debit: difference, credit: 0 };
    if (difference < 0) return { ...entry, debit: 0, credit: Math.abs(difference) };
    return { ...entry, debit: 0, credit: 0 };
  }
  return {
    ...entry,
    debit: Math.max(debit, -credit),
    credit: Math.max(credit, -debit),
  };
};

export const getOpeningCashTotal = (rdata) => {
  const rows = rdata.openingCashbook || [];
  if (!rows.length) return 0;
  // Backend may return SUM as [{ amount }] or row list
  if (rows.length === 1 && rows[0].amount !== undefined && rows[0].id === undefined) {
    return parseFloat(rows[0].amount) || 0;
  }
  return sumArray(rows.map((r) => parseFloat(r.amount)));
};

export const getBankBalance = (rdata) => {
  const cumulative = parseFloat(rdata.cumulativeCash?.[0]?.difference);
  if (rdata.cumulativeCash?.[0]?.difference !== undefined && rdata.cumulativeCash?.[0]?.difference !== null && !Number.isNaN(cumulative)) {
    return cumulative;
  }
  const opening = parseFloat(rdata.openingCash?.[0]?.difference) || 0;
  const period = parseFloat(rdata.cash?.[0]?.difference) || 0;
  return opening + period;
};

/**
 * Build P&L lines with the same posting rules as Trial Balance:
 *   Bills            → always revenue
 *   Supplier invoices → always expense (unless balance-sheet account)
 *   Journals         → only revenue/expense accounts
 * Line keys are type+code so an expense can never appear under revenue.
 */
const buildIncomeLines = (bills, supplier, journals, allData) => {
  const dataMap = {};
  let revenueTotal = 0;
  let expensesTotal = 0;

  const isBalanceSheetAccount = (code) => {
    const cls = classifyAccount(code, allData);
    return cls === 'asset' || cls === 'liability' || cls === 'equity';
  };

  const addLine = (code, amount, type) => {
    if (!code || isControlAccount(code) || amount === 0) return;
    const key = `${type}:${code}`;
    const name = getCoaName(code, allData);

    if (!dataMap[key]) {
      dataMap[key] = { code, name, amount: 0, type };
    }
    dataMap[key].amount += amount;

    if (type === 'revenue') revenueTotal += amount;
    else expensesTotal += amount;
  };

  // Same rule as TB: bills always revenue
  (bills || []).forEach((res) => {
    const code = getBudgetCoa(res.coa_id, allData);
    if (!code || isControlAccount(code)) return;
    addLine(code, parseFloat(res.amount) || 0, 'revenue');
  });

  // Same rule as TB: supplier invoices always expense-side,
  // but keep capital/BS purchases off the income statement
  (supplier || [])
    .filter((a) => String(a.type) === '1')
    .forEach((res) => {
      let code;
      if (res.expense && res.expense !== 'None') {
        code = String(res.expense);
      } else {
        code = getBudgetCoa(res.coa, allData);
      }
      if (!code || isControlAccount(code)) return;
      if (isBalanceSheetAccount(code)) return;
      addLine(code, parseFloat(res.amount) || 0, 'expense');
    });

  (journals || []).forEach((entry) => {
    const amount = parseFloat(entry.amount) || 0;
    const fromCode = getBudgetCoa(entry.from_coa, allData);
    const toCode = getBudgetCoa(entry.to_coa, allData);

    const applyJournalSide = (code, signed) => {
      if (!code || isControlAccount(code) || isBalanceSheetAccount(code)) return;
      const cls = classifyAccount(code, allData);
      // Mirror TB: unknown defaults to expense; only pure revenue stays revenue
      const type = cls === 'revenue' ? 'revenue' : 'expense';
      addLine(code, signed, type);
    };

    applyJournalSide(fromCode, -amount);
    applyJournalSide(toCode, amount);
  });

  // Negative revenue (credit notes / reversals) → move to expenses as positive amounts
  Object.keys(dataMap).forEach((key) => {
    const line = dataMap[key];
    if (line.type !== 'revenue' || line.amount >= 0) return;

    const expenseAmount = Math.abs(line.amount);
    // Undo the negative amount previously added to revenue
    revenueTotal -= line.amount;
    delete dataMap[key];

    const expKey = `expense:${line.code}`;
    if (!dataMap[expKey]) {
      dataMap[expKey] = { code: line.code, name: line.name, amount: 0, type: 'expense' };
    }
    dataMap[expKey].amount += expenseAmount;
    expensesTotal += expenseAmount;
  });

  return {
    lines: Object.values(dataMap)
      .filter((l) => l.amount !== 0)
      .sort((a, b) => String(a.code).localeCompare(String(b.code))),
    totalRevenue: revenueTotal,
    totalExpenses: expensesTotal,
    netIncome: revenueTotal - expensesTotal,
  };
};

export const calculateIncomeStatement = (rdata, allData, { cumulative = false } = {}) => {
  if (cumulative) {
    return buildIncomeLines(
      mergeBills(rdata.openingBills, rdata.bills),
      mergeSupplier(rdata.openingSupplier, rdata.supplier),
      mergeJournals(rdata.openingJournal, rdata.journal),
      allData
    );
  }

  const period = buildIncomeLines(rdata.bills, rdata.supplier, rdata.journal, allData);
  const opening = buildIncomeLines(
    rdata.openingBills,
    rdata.openingSupplier,
    rdata.openingJournal,
    allData
  );

  return {
    ...period,
    openingSurplus: opening.netIncome,
    openingRevenue: opening.totalRevenue,
    openingExpenses: opening.totalExpenses,
    // Opening cash (Accumulated Fund) — equity b/f, not P&L
    openingCash: getOpeningCashTotal(rdata),
    totalSurplus: getOpeningCashTotal(rdata) + opening.netIncome + period.netIncome,
  };
};

export const calculateAccumulatedFund = (rdata) => getOpeningCashTotal(rdata);

/**
 * Explain TB imbalance: cashbook movements that have no matching AR/AP.
 * difference ≈ (cashIncome - receipts) - (cashExpense - supplierPayments)
 * Transfers and opening balances are excluded from this check.
 */
export const diagnoseTrialBalance = (rdata) => {
  const cashIncome = parseFloat(rdata.diagnostics?.cashIncome) || 0;
  const cashExpense = parseFloat(rdata.diagnostics?.cashExpense) || 0;
  const receiptTotal = mergeScalarAmount(rdata.openingRecs, rdata.recs);
  const cumulativeSupplier = mergeSupplier(rdata.openingSupplier, rdata.supplier);
  const supplierPayments = sumArray(
    cumulativeSupplier.filter((a) => String(a.type) === '2').map((s) => parseFloat(s.amount))
  );
  const incomeGap = cashIncome - receiptTotal;
  const expenseGap = cashExpense - supplierPayments;
  const estimatedImbalance = incomeGap - expenseGap;

  return {
    cashIncome,
    cashExpense,
    receiptTotal,
    supplierPayments,
    incomeGap,
    expenseGap,
    estimatedImbalance,
  };
};

export const calculateTrialBalance = (rdata, allData) => {
  const balances = new Map();

  const cumulativeBills = mergeBills(rdata.openingBills, rdata.bills);
  const cumulativeSupplier = mergeSupplier(rdata.openingSupplier, rdata.supplier);
  const cumulativeJournals = mergeJournals(rdata.openingJournal, rdata.journal);
  const totalBills = sumArray(cumulativeBills.map((b) => parseFloat(b.amount)));
  const totalRecs = mergeScalarAmount(rdata.openingRecs, rdata.recs);
  let bankBalance = getBankBalance(rdata);

  cumulativeBills.forEach((res) => {
    const code = getBudgetCoa(res.coa_id, allData);
    if (!code) return;
    // Bills are always revenue → credit side
    addSignedAmount(balances, code, parseFloat(res.amount) || 0, allData, 'revenue');
  });

  cumulativeSupplier
    .filter((a) => String(a.type) === '1')
    .forEach((res) => {
      // Supplier invoices are always expense/asset → debit side
      // (avoids misclassified COA types posting to credit and breaking the TB)
      if (res.expense && res.expense !== 'None') {
        addSignedAmount(balances, res.expense, parseFloat(res.amount) || 0, allData, 'expense');
        return;
      }
      const code = getBudgetCoa(res.coa, allData);
      if (!code) return;
      addSignedAmount(balances, code, parseFloat(res.amount) || 0, allData, 'expense');
    });

  let journalBankAdj = 0;
  cumulativeJournals.forEach((entry) => {
    journalBankAdj += applyJournalEntry(balances, entry, allData);
  });
  // Do NOT add journalBankAdj to cashbook bank balance — imprest journals
  // already create cashbook rows. Pure bank journals without cashbook will
  // surface in the suspense difference below.

  const debtors = totalBills - totalRecs;
  const supplierInvoices = sumArray(
    cumulativeSupplier.filter((a) => String(a.type) === '1').map((s) => parseFloat(s.amount))
  );
  const supplierPayments = sumArray(
    cumulativeSupplier.filter((a) => String(a.type) === '2').map((s) => parseFloat(s.amount))
  );
  const creditors = supplierInvoices - supplierPayments;
  const accumulatedFund = calculateAccumulatedFund(rdata);
  const diagnosis = diagnoseTrialBalance(rdata);

  const controlRows = [
    {
      code: CONTROL_ACCOUNTS.DEBTORS,
      desc: getCoaName(CONTROL_ACCOUNTS.DEBTORS, allData),
      debit: debtors > 0 ? debtors : 0,
      credit: debtors < 0 ? Math.abs(debtors) : 0,
    },
    {
      code: CONTROL_ACCOUNTS.BANK,
      desc: getCoaName(CONTROL_ACCOUNTS.BANK, allData),
      debit: bankBalance > 0 ? bankBalance : 0,
      credit: bankBalance < 0 ? Math.abs(bankBalance) : 0,
    },
    {
      code: CONTROL_ACCOUNTS.ACCUMULATED_FUND,
      desc: getCoaName(CONTROL_ACCOUNTS.ACCUMULATED_FUND, allData),
      debit: accumulatedFund < 0 ? Math.abs(accumulatedFund) : 0,
      credit: accumulatedFund > 0 ? accumulatedFund : 0,
    },
    {
      code: CONTROL_ACCOUNTS.CREDITORS,
      desc: getCoaName(CONTROL_ACCOUNTS.CREDITORS, allData),
      debit: creditors < 0 ? Math.abs(creditors) : 0,
      credit: creditors > 0 ? creditors : 0,
    },
  ];

  let rows = [
    ...Array.from(balances.values())
      .filter((r) => !isControlAccount(r.code))
      .map(netTrialBalanceRow),
    ...controlRows.map(netTrialBalanceRow),
  ]
    .filter((r) => r.debit !== 0 || r.credit !== 0)
    .sort((a, b) => String(a.code).localeCompare(String(b.code)));

  let totalDebit = rows.reduce((s, r) => s + (r.debit || 0), 0);
  let totalCredit = rows.reduce((s, r) => s + (r.credit || 0), 0);

  // Post residual to suspense so TB balances and the gap is visible (often ~orphaned cash).
  const residual = totalDebit - totalCredit;
  if (Math.abs(residual) >= 0.05) {
    rows.push(netTrialBalanceRow({
      code: '999999',
      desc: 'Suspense (unmatched cash / incomplete deletes / imprest without receipt)',
      debit: residual < 0 ? Math.abs(residual) : 0,
      credit: residual > 0 ? residual : 0,
    }));
    rows = rows.sort((a, b) => String(a.code).localeCompare(String(b.code)));
    totalDebit = rows.reduce((s, r) => s + (r.debit || 0), 0);
    totalCredit = rows.reduce((s, r) => s + (r.credit || 0), 0);
  }

  return {
    rows,
    totalDebit,
    totalCredit,
    isBalanced: Math.abs(totalDebit - totalCredit) < 0.05,
    diagnosis: {
      ...diagnosis,
      residualBeforeSuspense: residual,
      journalBankAdj,
    },
  };
};

export const calculateFinancialPosition = (rdata, allData) => {
  const cumulativeBills = mergeBills(rdata.openingBills, rdata.bills);
  const cumulativeSupplier = mergeSupplier(rdata.openingSupplier, rdata.supplier);
  const totalBills = sumArray(cumulativeBills.map((b) => parseFloat(b.amount)));
  const totalRecs = mergeScalarAmount(rdata.openingRecs, rdata.recs);
  const bankBalance = getBankBalance(rdata);
  const debtors = totalBills - totalRecs;
  const supplierInvoices = sumArray(
    cumulativeSupplier.filter((a) => String(a.type) === '1').map((s) => parseFloat(s.amount))
  );
  const supplierPayments = sumArray(
    cumulativeSupplier.filter((a) => String(a.type) === '2').map((s) => parseFloat(s.amount))
  );
  const creditors = supplierInvoices - supplierPayments;
  const accumulatedFund = calculateAccumulatedFund(rdata);
  const incomeStatement = calculateIncomeStatement(rdata, allData, { cumulative: false });
  const surplus = incomeStatement.netIncome;
  const openingSurplus = incomeStatement.openingSurplus || 0;

  const assetLines = [];
  let totalAssets = 0;

  const addAssetLine = (name, amount) => {
    if (amount === 0) return;
    assetLines.push({ name, amount });
    totalAssets += amount;
  };

  cumulativeSupplier
    .filter((a) => String(a.type) === '1')
    .forEach((res) => {
      let code;
      if (res.expense && res.expense !== 'None') {
        code = res.expense;
      } else {
        code = getBudgetCoa(res.coa, allData);
      }
      if (!code || isControlAccount(code)) return;
      const cls = classifyAccount(code, allData);
      if (cls !== 'asset') return;
      addAssetLine(getCoaName(code, allData), parseFloat(res.amount) || 0);
    });

  addAssetLine(getCoaName(CONTROL_ACCOUNTS.DEBTORS, allData), debtors);
  addAssetLine(getCoaName(CONTROL_ACCOUNTS.BANK, allData), bankBalance);

  const totalEquity = accumulatedFund + openingSurplus + surplus;
  const totalLiabilitiesEquity = creditors + totalEquity;

  return {
    assetLines,
    totalAssets,
    creditors,
    accumulatedFund,
    openingSurplus,
    surplus,
    totalEquity,
    totalLiabilitiesEquity,
    isBalanced: Math.abs(totalAssets - totalLiabilitiesEquity) < 0.05,
  };
};
